import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export type TaskCancelMode = "cancel" | "request";

interface TaskCancelDialogProps {
  open: boolean;
  mode: TaskCancelMode;
  taskTitle: string;
  hasSubtasks: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason?: string) => Promise<void>;
}

export function TaskCancelDialog({
  open,
  mode,
  taskTitle,
  hasSubtasks,
  onOpenChange,
  onConfirm,
}: TaskCancelDialogProps) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const isRequest = mode === "request";

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      await onConfirm(reason.trim() || undefined);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isRequest ? "Solicitar exclusão" : "Cancelar tarefa?"}
          </DialogTitle>
          <DialogDescription className="line-clamp-2">{taskTitle}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {isRequest ? (
            <>
              <p className="text-sm text-muted-foreground">
                Esta tarefa foi criada por um gestor. Sua solicitação será enviada
                para aprovação.
              </p>
              <div className="space-y-2">
                <Label htmlFor="cancel-reason">Motivo (opcional)</Label>
                <Textarea
                  id="cancel-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Explique por que deseja excluir esta tarefa..."
                />
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              A tarefa será marcada como cancelada.
              {hasSubtasks && " As subtarefas também serão canceladas."}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Voltar
          </Button>
          <Button
            variant={isRequest ? "default" : "destructive"}
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isRequest ? "Enviar solicitação" : "Cancelar tarefa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
